import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';

const DIM_COLORS = {
  strategy:'#06b6d4', decision:'#7c3aed', process:'#f59e0b', accountability:'#f97316',
  execution:'#22c55e', financial:'#10b981', hr_culture:'#ec4899', sales_marketing:'#f43f5e',
  operations:'#0ea5e9', technology:'#8b5cf6', risk:'#dc2626', customer_exp:'#d946ef'
};

function QuestionRow({ q, dimKey, onUpdate, onRevert, onToggle, showToast }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(q.current);
  const [saving, setSaving]   = useState(false);
  const inputRef = useRef();

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const save = async () => {
    if (!draft.trim() || draft === q.current) { setEditing(false); return; }
    setSaving(true);
    await onUpdate(dimKey, q.q_index, draft.trim());
    setSaving(false);
    setEditing(false);
    showToast('Question updated');
  };

  const revert = async () => {
    if (!window.confirm('Revert to original question?')) return;
    await onRevert(q.override_id);
    setDraft(q.original);
    showToast('Reverted to original');
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0',
      borderBottom: '0.5px solid var(--border)',
      opacity: q.is_active ? 1 : 0.45
    }}>
      <div style={{ fontFamily: 'var(--ff-m)', fontSize: 10, color: 'var(--text3)', width: 26, flexShrink: 0, paddingTop: 3 }}>
        Q{q.q_index + 1}
      </div>
      <div style={{ flex: 1 }}>
        {editing ? (
          <div>
            <textarea
              ref={inputRef}
              className="form-textarea"
              style={{ minHeight: 60, fontSize: 13, marginBottom: 8 }}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); } if (e.key === 'Escape') setEditing(false); }}
            />
            <div style={{ display: 'flex', gap: 7 }}>
              <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }} disabled={saving} onClick={save}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => { setEditing(false); setDraft(q.current); }}>Cancel</button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>{q.current}</div>
            {q.is_modified && (
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3, fontStyle: 'italic' }}>
                Original: "{q.original}"
              </div>
            )}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 5, flexShrink: 0, paddingTop: 2 }}>
        {q.is_modified && !editing && (
          <button
            className="btn btn-ghost btn-sm"
            style={{ fontSize: 10, padding: '2px 7px', color: 'var(--orange)', borderColor: 'rgba(249,115,22,0.3)' }}
            onClick={revert} title="Revert to original">
            ↺
          </button>
        )}
        {!editing && (
          <button
            className="btn btn-ghost btn-sm"
            style={{ fontSize: 10, padding: '2px 7px' }}
            onClick={() => setEditing(true)}>
            ✏️
          </button>
        )}
        <div
          className={`toggle ${q.is_active ? 'on' : ''}`}
          style={{ width: 32, height: 18 }}
          title={q.is_active ? 'Active — click to disable' : 'Disabled — click to enable'}
          onClick={() => onToggle(dimKey, q.q_index, q.override_id, !q.is_active)}
        />
      </div>
      {q.is_modified && (
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--gold)', marginTop: 5, flexShrink: 0 }} title="Modified" />
      )}
    </div>
  );
}

function CustomQuestionRow({ q, onEdit, onDelete, onToggle, showToast }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(q.question);

  const save = async () => {
    if (!draft.trim()) return;
    await onEdit(q.id, draft.trim());
    setEditing(false);
    showToast('Custom question updated');
  };

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: '0.5px solid var(--border)', opacity: q.is_active ? 1 : 0.45 }}>
      <div style={{ fontFamily: 'var(--ff-m)', fontSize: 9, color: 'var(--accent2)', width: 26, flexShrink: 0, paddingTop: 3 }}>
        +
      </div>
      <div style={{ flex: 1 }}>
        {editing ? (
          <div>
            <textarea className="form-textarea" style={{ minHeight: 56, fontSize: 13, marginBottom: 8 }}
              value={draft} onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); } if (e.key === 'Escape') setEditing(false); }} />
            <div style={{ display: 'flex', gap: 7 }}>
              <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }} onClick={save}>Save</button>
              <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => { setEditing(false); setDraft(q.question); }}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>{q.question}</div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 5, flexShrink: 0, paddingTop: 2 }}>
        {!editing && <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '2px 7px' }} onClick={() => setEditing(true)}>✏️</button>}
        <button className="btn btn-danger btn-sm" style={{ fontSize: 10, padding: '2px 7px' }} onClick={() => { if (window.confirm('Delete this custom question?')) onDelete(q.id); }}>✕</button>
        <div className={`toggle ${q.is_active ? 'on' : ''}`} style={{ width: 32, height: 18 }} onClick={() => onToggle(q.id, !q.is_active)} />
      </div>
    </div>
  );
}

export default function AdminQuestions({ showToast }) {
  const { authFetch } = useAuth();
  const [library, setLibrary]       = useState([]);
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [activeDim, setActiveDim]   = useState('');
  const [search, setSearch]         = useState('');
  const [filterMod, setFilterMod]   = useState(false);
  const [newQ, setNewQ]             = useState('');
  const [addingTo, setAddingTo]     = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [libRes, statRes] = await Promise.all([
        authFetch('/api/questions'),
        authFetch('/api/questions/stats')
      ]);
      const libData  = await libRes.json();
      const statData = await statRes.json();
      setLibrary(libData.library || []);
      setStats(statData);
      if (!activeDim && libData.library?.length) setActiveDim(libData.library[0].dim_key);
    } catch { showToast('Failed to load question library', 'error'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateOverride = async (dimKey, qIndex, question) => {
    await authFetch('/api/questions/override', {
      method: 'PUT', body: JSON.stringify({ dim_key: dimKey, q_index: qIndex, question })
    });
    await load();
  };

  const revertOverride = async (overrideId) => {
    await authFetch(`/api/questions/override/${overrideId}`, { method: 'DELETE' });
    await load();
  };

  const toggleOverride = async (dimKey, qIndex, overrideId, isActive) => {
    if (overrideId) {
      await authFetch('/api/questions/override', { method: 'PUT', body: JSON.stringify({ dim_key: dimKey, q_index: qIndex, is_active: isActive }) });
    } else {
      await authFetch('/api/questions/override', { method: 'PUT', body: JSON.stringify({ dim_key: dimKey, q_index: qIndex, is_active: isActive }) });
    }
    await load();
    showToast(isActive ? 'Question enabled' : 'Question disabled');
  };

  const addCustom = async (dimKey) => {
    if (!newQ.trim()) return;
    const res = await authFetch('/api/questions/custom', { method: 'POST', body: JSON.stringify({ dim_key: dimKey, question: newQ.trim() }) });
    if (res.ok) { showToast('Custom question added'); setNewQ(''); setAddingTo(''); await load(); }
    else { const d = await res.json(); showToast(d.error || 'Failed', 'error'); }
  };

  const editCustom = async (id, question) => {
    await authFetch(`/api/questions/custom/${id}`, { method: 'PUT', body: JSON.stringify({ question }) });
    await load();
  };

  const deleteCustom = async (id) => {
    await authFetch(`/api/questions/custom/${id}`, { method: 'DELETE' });
    showToast('Custom question deleted');
    await load();
  };

  const toggleCustom = async (id, isActive) => {
    await authFetch(`/api/questions/custom/${id}`, { method: 'PUT', body: JSON.stringify({ is_active: isActive }) });
    showToast(isActive ? 'Enabled' : 'Disabled');
    await load();
  };

  const resetDim = async (dimKey, label) => {
    if (!window.confirm(`Revert ALL overrides for "${label}"? Custom questions are kept.`)) return;
    const res = await authFetch(`/api/questions/reset-dim/${dimKey}`, { method: 'POST' });
    if (res.ok) { showToast(`All overrides for ${label} reverted`); await load(); }
    else showToast('Failed', 'error');
  };

  const activeDimData = library.find(d => d.dim_key === activeDim);

  const filteredQuestions = (activeDimData?.questions || []).filter(q => {
    if (filterMod && !q.is_modified) return false;
    if (search) return q.current.toLowerCase().includes(search.toLowerCase()) || q.original.toLowerCase().includes(search.toLowerCase());
    return true;
  });

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="page-title">Question Library Editor</div>
          <span className="admin-sidebar-badge">ADMIN</span>
        </div>
        <div className="page-sub">Edit, disable, or add questions to any of the 12 assessment dimensions — no code deploy needed</div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="g4" style={{ marginBottom: 20 }}>
          {[
            { label: 'Core Questions', val: stats.total_core, color: 'var(--text2)' },
            { label: 'Modified',       val: stats.overrides,  color: 'var(--gold)' },
            { label: 'Custom Added',   val: stats.custom,     color: 'var(--accent2)' },
            { label: 'Disabled',       val: stats.inactive,   color: 'var(--red)' },
          ].map(s => (
            <div key={s.label} className="card" style={{ padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontFamily: 'var(--ff-m)', fontSize: 26, fontWeight: 500, color: s.color, lineHeight: 1 }}>{s.val}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.8 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <input className="form-input" style={{ maxWidth: 260 }} placeholder="Search questions..."
          value={search} onChange={e => setSearch(e.target.value)} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--text2)', cursor: 'pointer' }}>
          <input type="checkbox" checked={filterMod} onChange={e => setFilterMod(e.target.checked)} />
          Show modified only
        </label>
        {stats?.overrides > 0 && (
          <span className="tag" style={{ color: 'var(--gold)', borderColor: 'rgba(245,166,35,0.3)', background: 'rgba(245,166,35,0.06)' }}>
            {stats.overrides} question{stats.overrides !== 1 ? 's' : ''} modified
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* Dimension sidebar */}
        <div style={{ width: 200, flexShrink: 0 }}>
          {library.map(dim => {
            const modCount = dim.questions.filter(q => q.is_modified).length;
            const customCount = dim.custom_questions.length;
            const inactiveCount = dim.questions.filter(q => !q.is_active).length;
            return (
              <div key={dim.dim_key}
                onClick={() => setActiveDim(dim.dim_key)}
                style={{
                  padding: '9px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                  background: activeDim === dim.dim_key ? `${DIM_COLORS[dim.dim_key] || '#00d4ff'}14` : 'transparent',
                  border: activeDim === dim.dim_key ? `1px solid ${DIM_COLORS[dim.dim_key] || '#00d4ff'}40` : '1px solid transparent',
                  transition: 'all 0.1s'
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontFamily: 'var(--ff-m)', fontSize: 9, color: DIM_COLORS[dim.dim_key] || 'var(--accent)', letterSpacing: 1 }}>{dim.code}</div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', marginTop: 1 }}>{dim.label}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {modCount > 0 && <span style={{ fontSize: 9, padding: '1px 5px', background: 'rgba(245,166,35,0.15)', color: 'var(--gold)', borderRadius: 10 }}>{modCount}</span>}
                    {customCount > 0 && <span style={{ fontSize: 9, padding: '1px 5px', background: 'rgba(167,139,250,0.15)', color: 'var(--accent2)', borderRadius: 10 }}>+{customCount}</span>}
                    {inactiveCount > 0 && <span style={{ fontSize: 9, padding: '1px 5px', background: 'rgba(239,68,68,0.15)', color: 'var(--red)', borderRadius: 10 }}>-{inactiveCount}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Question editor */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="loader" style={{ margin: '0 auto' }}></div></div>
          ) : activeDimData ? (
            <>
              {/* Dim header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: 'var(--ff-m)', fontSize: 10, color: DIM_COLORS[activeDim] || 'var(--accent)', letterSpacing: 1 }}>{activeDimData.code}</span>
                    <span style={{ fontFamily: 'var(--ff-d)', fontSize: 18, fontWeight: 700 }}>{activeDimData.label}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
                    {filteredQuestions.length} of {activeDimData.questions.length} core questions shown
                    {activeDimData.custom_questions.length > 0 && ` · ${activeDimData.custom_questions.length} custom`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {activeDimData.questions.some(q => q.is_modified) && (
                    <button className="btn btn-ghost btn-sm"
                      style={{ fontSize: 11, color: 'var(--orange)', borderColor: 'rgba(249,115,22,0.3)' }}
                      onClick={() => resetDim(activeDim, activeDimData.label)}>
                      ↺ Revert All
                    </button>
                  )}
                  <button className="btn btn-ghost btn-sm"
                    style={{ fontSize: 11, color: 'var(--accent2)', borderColor: 'rgba(167,139,250,0.3)' }}
                    onClick={() => setAddingTo(activeDim)}>
                    + Add Custom
                  </button>
                </div>
              </div>

              {/* Instructions */}
              <div style={{ padding: '10px 14px', background: 'rgba(0,212,255,0.04)', borderRadius: 8, border: '1px solid rgba(0,212,255,0.12)', marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.7 }}>
                  <strong style={{ color: 'var(--gold-2)' }}>How it works:</strong> Edit any question text for all future assessments. Disable to hide from respondents. Add custom questions for this dimension. Changes are live — no restart needed.
                  <br />Modified questions show their original text below. The <span style={{ fontFamily: 'var(--ff-m)', fontSize: 10 }}>↺</span> button reverts a single question; "Revert All" restores the full dimension.
                </div>
              </div>

              {/* Core questions */}
              <div className="card" style={{ padding: '4px 16px', marginBottom: 14 }}>
                {filteredQuestions.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 24, color: 'var(--text3)', fontSize: 13 }}>
                    No questions match your filter.
                  </div>
                ) : filteredQuestions.map(q => (
                  <QuestionRow
                    key={`${q.dim_key}-${q.q_index}`}
                    q={q}
                    dimKey={activeDim}
                    onUpdate={updateOverride}
                    onRevert={revertOverride}
                    onToggle={toggleOverride}
                    showToast={showToast}
                  />
                ))}
              </div>

              {/* Custom questions */}
              {activeDimData.custom_questions.length > 0 && (
                <>
                  <div className="sec-header">
                    <div className="sec-title" style={{ color: 'var(--accent2)' }}>Custom Questions</div>
                    <div className="sec-line"></div>
                    <span className="tag" style={{ fontSize: 10, color: 'var(--accent2)', borderColor: 'rgba(167,139,250,0.3)' }}>
                      {activeDimData.custom_questions.length} added
                    </span>
                  </div>
                  <div className="card" style={{ padding: '4px 16px', marginBottom: 14 }}>
                    {activeDimData.custom_questions.map(q => (
                      <CustomQuestionRow key={q.id} q={q}
                        onEdit={editCustom} onDelete={deleteCustom} onToggle={toggleCustom} showToast={showToast} />
                    ))}
                  </div>
                </>
              )}

              {/* Add custom question form */}
              {addingTo === activeDim && (
                <div className="card" style={{ borderColor: 'rgba(167,139,250,0.3)', background: 'rgba(167,139,250,0.04)' }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: 'var(--accent2)' }}>
                    Add custom question to {activeDimData.label}
                  </div>
                  <textarea
                    className="form-textarea"
                    style={{ marginBottom: 10, fontSize: 13 }}
                    placeholder="E.g. Our team has clear visibility into quarterly priorities and how they connect to company goals."
                    value={newQ}
                    onChange={e => setNewQ(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addCustom(activeDim); } }}
                    rows={2}
                    autoFocus
                  />
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>
                    Use the same 1–5 agree/disagree format as core questions. Keep it specific and measurable.
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary btn-sm" style={{ borderColor: 'rgba(167,139,250,0.4)', color: 'var(--accent2)' }}
                      disabled={!newQ.trim()} onClick={() => addCustom(activeDim)}>
                      Add Question
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setAddingTo(''); setNewQ(''); }}>Cancel</button>
                  </div>
                </div>
              )}

              {addingTo !== activeDim && (
                <button className="btn btn-ghost btn-sm"
                  style={{ fontSize: 11, color: 'var(--accent2)', borderColor: 'rgba(167,139,250,0.3)' }}
                  onClick={() => setAddingTo(activeDim)}>
                  + Add Custom Question to {activeDimData.label}
                </button>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Select a dimension from the left</div>
          )}
        </div>
      </div>
    </div>
  );
}
